import React from "react";
import { useTranslation } from "react-i18next";
import "./PremenstrualReportModal.css";

const SEVERITY_CONFIG = {
  légère:  { color: "#22c55e", bg: "#f0fdf4", labelKey: "cycleBot.premenstrualReportModal.severityLight",  emoji: "🟢" },
  modérée: { color: "#f59e0b", bg: "#fffbeb", labelKey: "cycleBot.premenstrualReportModal.severityModerate", emoji: "🟡" },
  sévère:  { color: "#ef4444", bg: "#fef2f2", labelKey: "cycleBot.premenstrualReportModal.severitySevere",  emoji: "🔴" },
};

const PRIORITY_CONFIG = {
  high:   { color: "#ef4444", bg: "#fef2f2", icon: "🔴", labelKey: "cycleBot.premenstrualReportModal.priorityHigh" },
  medium: { color: "#f59e0b", bg: "#fffbeb", icon: "🟡", labelKey: "cycleBot.premenstrualReportModal.priorityMedium" },
  low:    { color: "#22c55e", bg: "#f0fdf4", icon: "🟢", labelKey: "cycleBot.premenstrualReportModal.priorityLow" },
};

function resolveSeverityKey(value) {
  const v = (value || "").toString().trim().toLowerCase();
  if (["legere", "légère", "light", "خفيفة"].includes(v)) return "légère";
  if (["severe", "sévère", "شديدة"].includes(v)) return "sévère";
  return "modérée";
}

/* ── Jauge de bien-être ── */
function WellbeingGauge({ score }) {
  const color = score >= 70 ? "#22c55e" : score >= 40 ? "#f59e0b" : "#ef4444";
  const pct   = score || 0;
  return (
    <div className="pmr-gauge-wrap">
      <svg className="pmr-gauge-svg" viewBox="0 0 120 70">
        <path d="M10,60 A50,50 0 0,1 110,60" fill="none" stroke="#f3e8ff" strokeWidth="10" strokeLinecap="round" />
        <path
          d="M10,60 A50,50 0 0,1 110,60"
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={`${(pct / 100) * 157} 157`}
          style={{ transition: "stroke-dasharray 1s ease", filter: `drop-shadow(0 0 4px ${color}66)` }}
        />
      </svg>
      <div className="pmr-gauge-center">
        <span className="pmr-gauge-num" style={{ color }}>{score}</span>
        <span className="pmr-gauge-label">/100</span>
      </div>
    </div>
  );
}

/* ── Modal principal ── */
export default function PremenstrualReportModal({ report, onClose, loading }) {
  const { t, i18n } = useTranslation("cycle");
  const locale = i18n.resolvedLanguage || i18n.language;
  if (!report && !loading) return null;

  const sevKey = resolveSeverityKey(report?.severity);
  const sev    = SEVERITY_CONFIG[sevKey];
  const stats  = report?.stats || {};

  return (
    <div className="pmr-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="pmr-modal">

        {/* ── Header ── */}
        <div className="pmr-header">
          <div className="pmr-header-left">
            <span className="pmr-header-icon">📋</span>
            <div>
              <h2>{t("cycleBot.premenstrualReportModal.title")}</h2>
              <p>
                {report?.generated_at
                  ? new Date(report.generated_at).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" })
                  : t("cycleBot.premenstrualReportModal.generating")}
                {report?.next_j1_date && ` · ${t("cycleBot.premenstrualReportModal.nextJ1", { date: new Date(report.next_j1_date + "T00:00:00").toLocaleDateString(locale, { day: "numeric", month: "long" }) })}`}
              </p>
            </div>
          </div>
          <button className="pmr-close" onClick={onClose}>✕</button>
        </div>

        {/* ── Loading ── */}
        {loading && (
          <div className="pmr-loading">
            <div className="pmr-loading-orb">
              <div className="pmr-loading-ring" />
              <span className="pmr-loading-icon">🤖</span>
            </div>
            <p className="pmr-loading-title">{t("cycleBot.premenstrualReportModal.loadingAnalysis")}</p>
          </div>
        )}

        {/* ── Contenu ── */}
        {!loading && report && (
          <div className="pmr-body">

            {/* Sévérité + Score */}
            <div className="pmr-top-row">
              <div className="pmr-severity-card" style={{ background: sev.bg, borderColor: sev.color + "40" }}>
                <span className="pmr-sev-emoji">{sev.emoji}</span>
                <div>
                  <div className="pmr-sev-label" style={{ color: sev.color }}>
                    {t("cycleBot.premenstrualReportModal.intensity", { level: t(sev.labelKey) })}
                  </div>
                  <div className="pmr-sev-sub">{t("cycleBot.premenstrualReportModal.trackedDays", { count: report.entries_count || 0 })}</div>
                </div>
              </div>

              <div className="pmr-wellbeing-card">
                <WellbeingGauge score={report.wellbeing_score} />
                <p className="pmr-gauge-title">{t("cycleBot.premenstrualReportModal.wellbeingScore")}</p>
              </div>
            </div>

            {/* Stats rapides */}
            {stats && (
              <div className="pmr-stats-row">
                {stats.avg_pain != null && (
                  <div className="pmr-stat">
                    <span className="pmr-stat-icon">💢</span>
                    <span className="pmr-stat-val">{stats.avg_pain}/10</span>
                    <span className="pmr-stat-lbl">{t("cycleBot.premenstrualReportModal.avgPain")}</span>
                  </div>
                )}
                {stats.avg_mood != null && (
                  <div className="pmr-stat">
                    <span className="pmr-stat-icon">🧠</span>
                    <span className="pmr-stat-val">{stats.avg_mood}/5</span>
                    <span className="pmr-stat-lbl">{t("cycleBot.premenstrualReportModal.avgMood")}</span>
                  </div>
                )}
                {stats.avg_energy != null && (
                  <div className="pmr-stat">
                    <span className="pmr-stat-icon">⚡</span>
                    <span className="pmr-stat-val">{stats.avg_energy}/5</span>
                    <span className="pmr-stat-lbl">{t("cycleBot.premenstrualReportModal.avgEnergy")}</span>
                  </div>
                )}
                {stats.top_symptoms?.length > 0 && (
                  <div className="pmr-stat pmr-stat--wide">
                    <span className="pmr-stat-icon">🔁</span>
                    <span className="pmr-stat-val pmr-stat-chips">
                      {stats.top_symptoms.slice(0, 3).map((s) => (
                        <span key={s} className="pmr-chip">{t(`cycleBot.journalView.symptom.${s}`, { defaultValue: s.replace(/_/g, " ") })}</span>
                      ))}
                    </span>
                    <span className="pmr-stat-lbl">{t("cycleBot.premenstrualReportModal.frequentSymptoms")}</span>
                  </div>
                )}
              </div>
            )}

            {/* Résumé */}
            {report.summary && (
              <div className="pmr-section">
                <h3>📝 {t("cycleBot.premenstrualReportModal.summary")}</h3>
                <p className="pmr-summary">{report.summary}</p>
              </div>
            )}

            {/* Red flags */}
            {report.red_flags?.length > 0 && (
              <div className="pmr-section pmr-red-flags">
                <h3>🚨 {t("cycleBot.premenstrualReportModal.attentionPoints")}</h3>
                {report.red_flags.map((flag, i) => (
                  <div key={i} className="pmr-flag">{flag}</div>
                ))}
              </div>
            )}

            {/* Analyse */}
            {report.analysis && (
              <div className="pmr-section">
                <h3>🔬 {t("cycleBot.premenstrualReportModal.analysis")}</h3>
                <p className="pmr-analysis">{report.analysis}</p>
              </div>
            )}

            {/* Recommandations */}
            {report.recommendations?.length > 0 && (
              <div className="pmr-section">
                <h3>💡 {t("cycleBot.premenstrualReportModal.recommendations")}</h3>
                {report.recommendations.map((rec, i) => {
                  const p = PRIORITY_CONFIG[rec.priority] || PRIORITY_CONFIG.medium;
                  return (
                    <div key={i} className="pmr-rec" style={{ borderLeftColor: p.color, background: p.bg }}>
                      <div className="pmr-rec-title">
                        <span>{p.icon}</span>
                        <strong>{rec.title}</strong>
                        <span>({t(p.labelKey)})</span>
                      </div>
                      <ul className="pmr-rec-items">
                        {rec.items?.map((item, j) => <li key={j}>{item}</li>)}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Conseils prochain cycle */}
            {report.next_cycle_tips?.length > 0 && (
              <div className="pmr-section">
                <h3>🔮 {t("cycleBot.premenstrualReportModal.nextCycleTips")}</h3>
                <ul className="pmr-tips">
                  {report.next_cycle_tips.map((tip, i) => (
                    <li key={i}>{tip}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Message IA */}
            {report.ai_message && (
              <div className="pmr-ai-message">
                <span className="pmr-ai-icon">🤖</span>
                <p>{report.ai_message}</p>
              </div>
            )}

            {/* Disclaimer */}
            <div className="pmr-disclaimer">
              <span>⚕️</span>
              <p>{report.disclaimer}</p>
            </div>
          </div>
        )}

        {/* Footer */}
        {!loading && (
          <div className="pmr-footer">
            <button className="pmr-close-btn" onClick={onClose}>{t("cycleBot.premenstrualReportModal.close")}</button>
          </div>
        )}
      </div>
    </div>
  );
}
